import React, { useContext, useEffect, useState } from "react"
import { graphql, PageProps } from "gatsby"
import { Layout } from "../components/atoms/Layout"
import GenericHero from "../components/organisms/Accessory/Hero/GenericHero"
import { SEO } from "../components/atoms/SEO"
import {
  ContentSlider,
  SeriesSelection,
} from "../components/organisms/Homepage"
import tw from "twin.macro"
import {
  Brochure,
  Features,
  ImageBlocks,
  ToyotaCare,
} from "../components/organisms/Series"
import { DisclaimersContext } from "../contexts/Disclaimers"
import { setDisclaimers } from "../contexts/Disclaimers/actions"
import useTealiumView from "../hooks/Tealium/useTealiumView"
import TactVideoSection from "../components/organisms/Tact/TactVideoSection"
import FAQ from "../components/molecules/FAQ/FAQ"
import { Gallery } from "../components/organisms/Series"
import useTealiumEvent from "../hooks/Tealium/useTealiumEvent"
import { ImageBlocksWithDescription } from "../components/molecules/ImageBlocksWithDescription"
import { VideoFeatureTabsSection } from "../components/molecules/VideoFeatureTabsSection"
import VideoWithText from "../components/molecules/VideoWithText/VideoWithText"
import TabbedImageModule from "../components/molecules/TabbedImageModule/TabbedImageModule"
import SegmentLanderHero from "../components/organisms/Segment/SegmentLanderHero/SegmentLanderHero"
import _ from "lodash"
import StackedImageModule from "../components/organisms/Segment/StackedImageModule/StackedImageModule"
import KeyFeaturesModule from "../components/organisms/Segment/KeyFeaturesModule/KeyFeaturesModule"
import WhatsNextSection from "../components/organisms/Segment/WhatsNextSection/WhatsNextSection"
import ModelHighlightsModule from "../components/organisms/Segment/ModelHighlightsModule/ModelHighlightsModule"
import AllVehicleExpandedSelectorModule from "../components/organisms/Segment/AllVehicleExpandedSelectorModule/AllVehicleExpandedSelectorModule"
import FamilyOffersSection from "../components/organisms/Segment/FamilyOffersSection/FamilyOffersSection"
import FeaturedSnippetModule from "../components/organisms/FeaturedSnippet/FeaturedSnippetModule"
import TwoImagesWithText from "../components/organisms/ComingSoon/TwoImagesWithText/TwoImagesWithText"
import { DealersNearYou } from "../components/organisms/DealersNearYou"
import { VideoBlocks } from "../components/molecules/VideoBlocks"
import { RetentionSalesEventHero } from "../components/molecules/RetentionSalesEventHero"

const SegmentPage: React.FC<PageProps> = ({ data, location }: any) => {
  const { page } = data
  const [state, dispatch] = useContext(DisclaimersContext)

  // Tealium
  const { handleTealView } = useTealiumView()
  const familyRef = page.familyRef[0]

  // Hybrids segment includes multiple hybrid categories. This assigns them the generic "hybrid".
  const familySegment = familyRef?.slug?.current?.includes("hybrid")
    ? "hybrids"
    : familyRef?.slug?.current

  useEffect(() => {
    if (page?.disclaimers?.length > 0) {
      const newDisclaimers = _.unionBy(
        page.disclaimers,
        state.disclaimers,
        "code",
      )
      dispatch(setDisclaimers(newDisclaimers))
    }

    handleTealView({
      tealium_event: `${familySegment}_lander`,
      page_name: familySegment.replace("_", " "),
      page_type: "segment",
      view: "view",
    })
  }, [])
  // End Tealium

  return (
    <Layout>
      <SEO
        url={location.href}
        title={page.title}
        keywords={page.seo?.keywords}
        schema={page.seo?.schema}
        description={page.seo?.description}
        canonicalPath={page.seo?.canonicalPath}
      />
      <SanitySections data={page} familySegment={familySegment} />
    </Layout>
  )
}
const SanitySections = ({ data, familySegment }: any): JSX.Element => {
  const retentionSalesEventData = data?.content?.find(
    (section: any) => section._type === "salesEventHeroPageBuilder",
  )?.salesEvent
  const retentionSalesEventHeroData = retentionSalesEventData?.salesEventActive
    ? retentionSalesEventData?.hero
    : null

  return (
    <>
      {data?.content?.map((section: any, index: number) => (
        <React.Fragment key={section?._key || `segment-section-${index}`}>
          {(() => {
            switch (section?._type) {
              case "segmentLanderHero": {
                return retentionSalesEventHeroData ? (
                  <>
                    <RetentionSalesEventHero {...retentionSalesEventHeroData} />
                  </>
                ) : (
                  <SegmentLanderHero {...section} />
                )
              }
              case "genericHero": {
                return retentionSalesEventHeroData ? (
                  <>
                    <RetentionSalesEventHero {...retentionSalesEventHeroData} />
                  </>
                ) : (
                  <GenericHero
                    {...section}
                    showGradient={false}
                    textInverted={true}
                    fromHybridSegment={familySegment === "hybrids"}
                    fromSegment={true}
                  />
                )
              }
              case "seriesSelection":
                return (
                  <SeriesSelection
                    {...section}
                    analyticsIdArrows="vehicle:vehicle"
                    fromHybridSegment={familySegment === "hybrids"}
                  />
                )
              case "stackedImageModule":
                return <StackedImageModule {...section} />
              case "keyFeaturesModule":
                return <KeyFeaturesModule {...section} />
              case "whatsNextSection":
                return <WhatsNextSection {...section} />
              case "modelHighlightsModule":
                return <ModelHighlightsModule {...section} />
              case "allVehicleExpandedSelectorSection":
                return <AllVehicleExpandedSelectorModule {...section} />
              case "familyOffersSection":
                return <FamilyOffersSection {...section} />
              case "imageWithText":
                return <ToyotaCare {...section} />
              case "brochure":
                return <Brochure {...section} />
              case "videoSection":
                return <TactVideoSection {...section} />
              case "tabbedImageModule":
                return <TabbedImageModule {...section} />
              case "gallery":
                return (
                  <Gallery
                    {...section}
                    fromHybridSegment={true}
                    showTitle={false}
                  />
                )
              case "faqSection":
                return <FAQ {...section} expanded showTitle={true} />
              case "imageBlocksWithDescriptionSection":
                return (
                  <ImageBlocksWithDescription
                    section={section}
                    analyticsIdArrows="explore:explore"
                  />
                )
              case "videoBlocks":
                return <VideoBlocks {...section} />
              case "imageBlocks":
                const imageBlocksData = section?.callouts
                return (
                  <ImageBlocks
                    imageBlocks={imageBlocksData}
                    _key={"Image Blocks"}
                    sectionSlug={{
                      current: "",
                    }}
                  />
                )
              case "videoFeatureTabsSection":
                return <VideoFeatureTabsSection {...section} />
              case "videoWithText":
                return <VideoWithText {...section} />
              case "featuredSnippetModule":
                return <FeaturedSnippetModule {...section} />
              case "features":
                return <Features {...section} />
              case "twoImagesWithText":
                return <TwoImagesWithText {...section} />
              case "dealersNearYouSection":
                return <DealersNearYou type="default" {...section} />
              default:
                return null
            }
          })()}
        </React.Fragment>
      ))}
    </>
  )
}

export const query = graphql`
  query SegmentPageQuery($id: String) {
    page: sanityPageSegment(id: { eq: $id }) {
      id
      language
      title
      seo {
        keywords
        schema
        description
        canonicalPath
      }
      familyRef {
        id
        icon
        descriptionEN
        descriptionES
        name
        isActive
        linkText
        linkTextES
        linkRoute {
          slug {
            current
          }
        }
        routes {
          seriesImage {
            ...SanityImageData
          }
          description
          descriptionES
          _key
          ctas {
            title
            buttonType
            externalUrl
            internalLink {
              slug {
                current
              }
            }
          }
          ctasES {
            title
            buttonType
            externalUrl
            internalLink {
              slug {
                current
              }
            }
          }
          route {
            slug {
              current
            }
            page {
              ... on SanityPageSeries {
                id
                title
                vehicle {
                  models {
                    ... on SanityVehicleModel {
                      featuredModel
                      model {
                        ... on SanityModel {
                          id
                          name
                          year
                          code
                          isHybridModel
                          isElectricModel
                          series {
                            name
                            msrp
                            mpgCity
                            mpgHighway
                            isHybridVehicle
                          }
                          costs {
                            baseMSRP
                          }
                        }
                        ... on SanityCustomModel {
                          id
                          name
                          _key
                          year
                          series {
                            name
                            msrp
                          }
                          jellybean {
                            asset {
                              altText
                              _id
                              gatsbyImageData
                            }
                          }
                        }
                      }
                    }
                    ... on SanityVehicleModelGroup {
                      _key
                      _type
                      models {
                        _key
                        featuredModel
                        model {
                          ... on SanityModel {
                            _id
                            name
                            year
                            code
                            isHybridModel
                            isElectricModel
                            series {
                              name
                              msrp
                              mpgCity
                              mpgHighway
                              isHybridVehicle
                              range
                            }
                            costs {
                              baseMSRP
                            }
                            jellybean {
                              asset {
                                altText
                                _id
                                gatsbyImageData(placeholder: NONE)
                              }
                            }
                          }
                          ... on SanityCustomModel {
                            _id
                            name
                            year
                            series {
                              name
                              msrp
                              mpgCity
                              mpgHighway
                              range
                            }
                            jellybean {
                              asset {
                                altText
                                _id
                                gatsbyImageData(placeholder: NONE)
                              }
                            }
                          }
                        }
                      }
                    }
                  }
                }
              }
            }
          }
        }
        slug {
          current
        }
      }
      content {
        ...SegementLanderHeroData
        ...KeyFeaturesModuleData
        ...StackedImageModuleData
        ...ModelHighlightsModuleData
        ...FamilyOffersSectionData
        ...WhatsNextSectionData
        ...AllVehicleExpandedSelectorSectionData
        ...GenericHeroData
        ...ImageBlocksData
        ...RetentionSalesEventImageWithTextData
        ...RetentionSalesEventHeroData
        ...ToyotaCareData
        ...SeriesSelectionData
        ...BrochureData
        ...VideoSectionData
        ...TabbedImageModuleData
        ...VideoBlocksDataWithMedia
        ...GallerySection
        ...FaqSectionData
        ...ImageBlocksWithDescriptionData
        ...VideoFeatureTabsSection
        ...VideoWithTextSection
        ...FeaturedSnippetModuleData
        ...FeaturesSectionData
        ...TwoImagesWithTextData
        ...DealersNearYouData
      }
      disclaimers {
        code
        disclaimer
      }
    }
  }
`

export default SegmentPage
